/**
 * PlanListPage - Table view of AI interview plans for researchers
 */

import { useState } from 'react'
import type { AIInterviewPlanTemplate } from '@ispot/shared'
import { usePlans, useDeletePlan, useActivatePlan, useDeactivatePlan } from '@/hooks/useAIInterviewPlans'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  ClipboardList,
  Pencil,
  Power,
  Trash2,
  Loader2,
  AlertCircle,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface PlanListPageProps {
  accountId: string
  projectId: string
  onSelectPlan: (planId: string) => void
  onCreatePlan: () => void
}

// ============================================================================
// STATUS BADGE
// ============================================================================

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-warning-bg text-warning-fg',
  active: 'bg-success-bg text-success-fg',
  closed: 'bg-danger-bg text-danger-fg',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge className={STATUS_STYLES[status] ?? 'bg-bg-secondary text-fg-secondary'}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  )
}

// ============================================================================
// EMPTY STATE
// ============================================================================

function EmptyState({ onCreatePlan }: { onCreatePlan: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-bg-card px-6 py-20 text-center">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-accent-50 text-accent-500">
        <ClipboardList className="h-5 w-5" />
      </div>
      <h3 className="mb-1 font-display text-heading font-semibold text-fg">
        No interview plans yet
      </h3>
      <p className="mb-6 max-w-sm text-body text-fg-secondary">
        Create a plan to structure your AI-assisted interviews with defined
        topics, questions, and session goals.
      </p>
      <Button onClick={onCreatePlan} className="bg-accent-500 text-bg-card hover:bg-accent-600">
        <Plus className="w-4 h-4 mr-2" />
        Create Plan
      </Button>
    </div>
  )
}

// ============================================================================
// PLAN ROW
// ============================================================================

function PlanRow({
  plan,
  onSelect,
  onToggle,
  onDelete,
}: {
  plan: AIInterviewPlanTemplate
  onSelect: () => void
  onToggle: () => void
  onDelete: () => void
}) {
  const created = new Date(plan.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <tr
      className="cursor-pointer border-b border-border-subtle transition-colors hover:bg-bg-secondary"
      onClick={onSelect}
    >
      <td className="px-4 py-3 text-body font-medium text-fg">
        {plan.title}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={plan.status} />
      </td>
      <td className="px-4 py-3 text-body text-fg-secondary tabular-nums">
        {plan.sessionCount ?? 0}
      </td>
      <td className="px-4 py-3 text-body text-fg-secondary">{created}</td>
      <td className="px-4 py-3">
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="ghost" size="icon" onClick={onSelect} title="Edit">
            <Pencil className="w-4 h-4 text-fg-muted" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            title={plan.status === 'active' ? 'Deactivate' : 'Activate'}
          >
            <Power
              className={`w-4 h-4 ${plan.status === 'active' ? 'text-success-fg' : 'text-fg-muted'}`}
            />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-danger-fg" />
          </Button>
        </div>
      </td>
    </tr>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PlanListPage({
  accountId,
  projectId,
  onSelectPlan,
  onCreatePlan,
}: PlanListPageProps) {
  const { data: plans, isLoading, error } = usePlans(accountId, projectId)
  const deletePlan = useDeletePlan(accountId, projectId)
  const activatePlan = useActivatePlan(accountId, projectId)
  const deactivatePlan = useDeactivatePlan(accountId, projectId)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  function handleToggle(plan: AIInterviewPlanTemplate) {
    if (plan.status === 'active') {
      deactivatePlan.mutate(plan.id)
    } else {
      activatePlan.mutate(plan.id)
    }
  }

  function handleDelete(planId: string) {
    setDeletingId(planId)
    deletePlan.mutate(planId, { onSettled: () => setDeletingId(null) })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-accent-500 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-body text-danger-fg">
        <AlertCircle className="w-4 h-4" />
        Failed to load plans. Please try again.
      </div>
    )
  }

  const hasPlan = plans && plans.length > 0

  return (
    <div className="min-h-full bg-bg px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-subhead font-semibold text-fg">Interview plans</h2>
          <p className="mt-1 text-caption text-fg-muted">
            {hasPlan ? `${plans.length} plans` : 'Reusable scripts and public links live here.'}
          </p>
        </div>
        {hasPlan && (
          <Button onClick={onCreatePlan} className="bg-accent-500 text-bg-card hover:bg-accent-600">
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        )}
      </div>

      {!hasPlan ? (
        <EmptyState onCreatePlan={onCreatePlan} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-bg-card">
          <table className="w-full min-w-[720px] text-left">
            <thead>
              <tr className="border-b border-border bg-bg-secondary">
                <th className="px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted">
                  Title
                </th>
                <th className="px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted">
                  Status
                </th>
                <th className="px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted">
                  Sessions
                </th>
                <th className="px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted">
                  Created
                </th>
                <th className="px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  onSelect={() => onSelectPlan(plan.id)}
                  onToggle={() => handleToggle(plan)}
                  onDelete={() => handleDelete(plan.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
